import { createSelector } from '@reduxjs/toolkit';
import { isString } from 'lodash-es';

import { objectEntries } from '@iconik-io/core/src/object_entries';
import type { CommonFiltersAppState } from '@iconik-io/shared-lib/src/redux/common_filters/types';

import {
  ADMIN_JOBS_DASHBOARD_SYSTEM_WIDGET_PAGE_ROUTE,
  ADMIN_JOBS_DASHBOARD_WIDGET_PAGE_ROUTE,
} from '../../constants/routes';
import type { CommonListPrefix } from '../../ducks/common_list/types';
import { getJobWidgetsCommonListPrefix } from '../../ducks/common_list/utils';
import { selectWidgetsMap } from '../../ducks/jobs_widgets/selectors';
import type { AppState } from '../../store/types';

const getWidgetStateFilters = (state: AppState) => state.jobsWidgetsFilters;

const widgetRoutes = [
  ADMIN_JOBS_DASHBOARD_SYSTEM_WIDGET_PAGE_ROUTE,
  ADMIN_JOBS_DASHBOARD_WIDGET_PAGE_ROUTE,
];

const getEntityId = (_state: AppState, entityId: string | undefined) =>
  entityId;

// Route and id are supplied together by the caller so they always describe the
// same match; see PanelsContainer.
const getRoute = (
  _state: AppState,
  _entityId: string | undefined,
  route: string,
) => route;

const selectWidgetState = createSelector(
  getRoute,
  getEntityId,
  selectWidgetsMap,
  (route, widgetId, widgetsMap) => {
    if (!widgetRoutes.includes(route) || !widgetId) {
      return undefined;
    }
    return widgetsMap[widgetId];
  },
);

export const selectWidgetFilters = createSelector(
  [getWidgetStateFilters, selectWidgetState],
  (stateFilters, widgetState) => {
    if (!widgetState) {
      return undefined;
    }
    const stateWidgetFilter = stateFilters[widgetState.id];
    const filters: CommonFiltersAppState = {
      query: '',
      availableFacets: {},
      filterValues: stateWidgetFilter.filterValues,
      facetValues: {},
      unsupportedFilters: [],
      rawCriteria: undefined,
    };
    // Facet names come off the API, so only some of them name a widget filter.
    const widgetFilters: Record<string, unknown> = widgetState.options.filters;

    objectEntries(stateWidgetFilter.availableFacets).forEach(
      ([facetKey, facet]) => {
        const settingValue = widgetFilters[facetKey];
        if (
          !settingValue ||
          (Array.isArray(settingValue) && settingValue.length === 0) ||
          (isString(settingValue) && settingValue.length === 0)
        ) {
          filters.availableFacets[facetKey] = facet;
        }
      },
    );

    return filters;
  },
);

const getFiltersState = (state: AppState) => state.panelsState.adminJobsFilters;
const getListState = (state: AppState) => state.pages.adminJobs;
const getJobsWidgetsLists = (state: AppState) => state.jobsWidgetsLists;

export const selectJobsFiltersState = createSelector(
  getFiltersState,
  getListState,
  getJobsWidgetsLists,
  selectWidgetState,
  selectWidgetFilters,
  (
    adminJobsFilters,
    listState,
    jobsWidgetsLists,
    widgetState,
    widgetFiltersState,
  ) => {
    let prefix: CommonListPrefix = 'admin_jobs_history';
    let filtersState = adminJobsFilters;
    let list = listState;
    if (widgetState && widgetFiltersState) {
      prefix = getJobWidgetsCommonListPrefix(widgetState.id);
      list = jobsWidgetsLists[widgetState.id];
      filtersState = widgetFiltersState;
    }
    return {
      commonListPrefix: prefix,
      filtersPanelState: filtersState,
      listState: list,
      widget: widgetState,
    };
  },
);
